const FA_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']

export const JALALI_MONTHS = [
    'فروردین',
    'اردیبهشت',
    'خرداد',
    'تیر',
    'مرداد',
    'شهریور',
    'مهر',
    'آبان',
    'آذر',
    'دی',
    'بهمن',
    'اسفند'
]

/** Reference "today" for the prototype content: 13 Aban 1405. Keeps server and client renders identical. */
export const TODAY = '1405/08/13'

export const toFaDigits = (value: string | number): string => {
    return String(value).replace(/[0-9]/g, digit => FA_DIGITS[Number(digit)])
}

export const toLatinDigits = (value: string): string => {
    return value
        .replace(/[۰-۹]/g, digit => String(FA_DIGITS.indexOf(digit)))
        .replace(/[٠-٩]/g, digit => String(AR_DIGITS.indexOf(digit)))
}

export const formatNumber = (value: number): string => {
    const [integer, fraction] = String(value).split('.')
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '٬')

    return toFaDigits(fraction ? `${grouped}٫${fraction}` : grouped)
}

type JalaliParts = {
    year: number
    month: number
    day: number
}

export const parseJalali = (value: string): JalaliParts => {
    const [year, month, day] = toLatinDigits(value).split('/').map(Number)

    return { year, month, day }
}

const isLeapYear = (year: number): boolean => [1, 5, 9, 13, 17, 22, 26, 30].includes(year % 33)

const toDayNumber = ({ year, month, day }: JalaliParts): number => {
    let days = 0

    for (let current = 1390; current < year; current++) {
        days += isLeapYear(current) ? 366 : 365
    }

    const monthOffset = month <= 6 ? (month - 1) * 31 : 186 + (month - 7) * 30

    return days + monthOffset + day
}

export const formatJalali = (value: string, withYear: boolean = true): string => {
    const { year, month, day } = parseJalali(value)
    const base = `${toFaDigits(day)} ${JALALI_MONTHS[month - 1] ?? ''}`

    return withYear ? `${base} ${toFaDigits(year)}` : base
}

export const daysSince = (value: string, reference: string = TODAY): number => {
    return toDayNumber(parseJalali(reference)) - toDayNumber(parseJalali(value))
}

export const compareJalali = (a: string, b: string): number => {
    return toDayNumber(parseJalali(a)) - toDayNumber(parseJalali(b))
}

export const relativeDate = (value: string): string => {
    const diff = daysSince(value)

    if (diff <= 0) return 'امروز'
    if (diff === 1) return 'دیروز'
    if (diff < 7) return `${toFaDigits(diff)} روز پیش`
    if (diff < 30) return `${toFaDigits(Math.floor(diff / 7))} هفته پیش`
    if (diff < 365) return `${toFaDigits(Math.floor(diff / 30))} ماه پیش`

    return `${toFaDigits(Math.floor(diff / 365))} سال پیش`
}

/** Client-only: the real current Jalali date, used when the visitor saves something. */
export const currentJalali = (): string => {
    try {
        const parts = new Intl.DateTimeFormat('en-US-u-ca-persian', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).formatToParts(new Date())

        const pick = (type: string): string => toLatinDigits(parts.find(part => part.type === type)?.value ?? '').replace(/\D/g, '')
        const year = pick('year')
        const month = pick('month').padStart(2, '0')
        const day = pick('day').padStart(2, '0')

        if (!year || !month || !day) return TODAY

        return `${year}/${month}/${day}`
    }
    catch {
        return TODAY
    }
}

export const formatBytes = (bytes: number): string => {
    if (bytes >= 1024 * 1024) return `${formatNumber(Math.round((bytes / (1024 * 1024)) * 10) / 10)} مگابایت`
    if (bytes >= 1024) return `${formatNumber(Math.round(bytes / 1024))} کیلوبایت`

    return `${formatNumber(bytes)} بایت`
}

export const readTime = (minutes: number): string => `${toFaDigits(minutes)} دقیقه مطالعه`

export const initials = (name: string): string => {
    const parts = name.trim().split(/\s+/)
    const first = parts[0]?.charAt(0) ?? ''
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''

    return last ? `${first}‌${last}` : first
}
